/** Resolve Part E `meeting_booking.id` from a `meeting_detail` list row. */
export function getBookingIdFromMeeting(meeting: any): number | null {
  if (!meeting) {
    return null;
  }

  if (meeting.booking_id != null && meeting.booking_id !== '') {
    const id = Number(meeting.booking_id);
    return Number.isFinite(id) ? id : null;
  }

  let details = meeting.google_meet_details;
  if (typeof details === 'string') {
    try {
      details = JSON.parse(details);
    } catch {
      return null;
    }
  }

  if (details?.booking_id != null && details.booking_id !== '') {
    const id = Number(details.booking_id);
    return Number.isFinite(id) ? id : null;
  }

  return null;
}

export function isPartEBookingMeeting(meeting: any): boolean {
  return getBookingIdFromMeeting(meeting) != null;
}

/** Build epoch ms for Asia/Kolkata from `YYYY-MM-DD` + `HH:mm` or `HH:mm - HH:mm`. */
export function meetingTimestampFromDateAndTime(
  date: string,
  time: string,
  timezone = 'Asia/Kolkata'
): number | null {
  if (!date || !time) {
    return null;
  }

  const startPart = String(time).split(' - ')[0]?.trim();
  if (!startPart || !/^\d{1,2}:\d{2}$/.test(startPart)) {
    return null;
  }

  if (timezone === 'Asia/Kolkata') {
    const isoLike = `${date}T${startPart}:00+05:30`;
    const ts = Date.parse(isoLike);
    return Number.isNaN(ts) ? null : ts;
  }

  const ts = Date.parse(`${date}T${startPart}:00`);
  return Number.isNaN(ts) ? null : ts;
}

export interface MeetingCardData {
  title?: string;
  desc?: string;
  date?: string;
  time?: string;
  link?: string;
  recipients?: string[];
  status?: string;
  duration?: string;
  organizer?: string;
}

function meetingOrganizerName(user: Record<string, unknown> | null | undefined): string {
  if (!user || typeof user !== 'object') {
    return '';
  }
  return (
    [user['prefix'], user['fname'], user['lname']].filter(Boolean).join(' ').trim() ||
    String(user['email'] || '').trim()
  );
}

/** User-friendly labels for meeting timeline cards. */
export function buildMeetingCardData(
  meeting: Record<string, unknown>
): MeetingCardData {
  const title = String(meeting['meeting_title'] || meeting['subject'] || '').trim();
  const desc = String(meeting['meeting_desc'] || meeting['description'] || '').trim();
  const date = String(meeting['meeting_date'] || meeting['appointment_date'] || '').trim();
  const time = String(meeting['meeting_time'] || meeting['appointment_time'] || '').trim();
  const link = String(meeting['meeting_link'] || meeting['meet_url'] || '').trim();
  const recipients = Array.isArray(meeting['recipients'])
    ? meeting['recipients'].map((r) => String(r).trim()).filter(Boolean)
    : [];

  const status = meeting['status'] ? String(meeting['status']).trim() : '';
  const durationRaw = meeting['duration_minutes'] ?? meeting['duration'];
  const duration =
    durationRaw != null && durationRaw !== '' ? `${durationRaw} min` : '';

  const organizer = meetingOrganizerName(
    meeting['user'] as Record<string, unknown> | null | undefined
  );

  const card: MeetingCardData = {};
  if (title) card.title = title;
  if (desc) card.desc = desc;
  if (date) card.date = date;
  if (time) card.time = time;
  if (link) card.link = link;
  if (recipients.length) card.recipients = recipients;
  if (status) card.status = status;
  if (duration) card.duration = duration;
  if (organizer) card.organizer = organizer;

  return card;
}
